import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getListingAvailableDateRange } from 'common/listingHelpers';
import { isPreLeaseActive } from 'common/preLeaseHelpers';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrIcon from 'client/components/SrIcon';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrCardOld from 'client/components/SrCardOld';
import SrEmoji from 'client/components/SrEmoji';
import SrSeparator from 'client/components/SrSeparator';

import UserActions from 'client/actions/User';

const dateFormat = 'MMM D';

class SrListingMoveInWindow extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    preLease: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    tenantScreeningCriteria: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  _onOpenMoveInDateModal = () => {
    const { dispatch } = this.props;
    dispatch(UserActions.setUpdateMoveInDateModalOpen, true);
  };

  render() {
    const { listing, preLease, tenantScreeningCriteria, currentUser } = this.props;
    const listingAvailableDateRange = getListingAvailableDateRange({
      listing,
      preLease,
      tenantScreeningCriteria,
    });
    const isPreLease = preLease && isPreLeaseActive({ preLease });

    const startDate = momentWithAustinTimezone(listingAvailableDateRange.startDate);
    const endDate = momentWithAustinTimezone(listingAvailableDateRange.endDate);
    const extendedDate = momentWithAustinTimezone(listingAvailableDateRange.extendedDate);
    const currentUserMoveInDate = currentUser.searchFilters.moveInDate
      ? momentWithAustinTimezone(currentUser.searchFilters.moveInDate)
      : null;

    let cardTitle = null;
    let cardEmoji = null;
    let cardColor = '';
    let tooptipLeftPosition = null;
    let isMoveInDateBeforeAvailableDate = false;

    const startDateFormatted = startDate.format(dateFormat);
    const endDateFormatted = endDate.format(dateFormat);
    const extendedDateFormatted = extendedDate.format(dateFormat);
    const currentUserMoveInDateFormatted =
      currentUserMoveInDate && currentUserMoveInDate.format(dateFormat);

    if (!currentUserMoveInDate) {
      cardTitle = (
        <div>
          You can move in <br />
          {startDate.isSame(momentWithAustinTimezone(), 'day') ? 'Today' : startDateFormatted}
          {' –  '}
          {endDate.format('MMM Do')}
        </div>
      );
      cardEmoji = '🗓';
      cardColor = 'blue';
    } else if (currentUserMoveInDate.isBefore(startDate)) {
      cardTitle = (
        <div>
          Your move date of <br />
          {currentUserMoveInDate.format(dateFormat)} doesn’t work.
        </div>
      );
      cardEmoji = '👎';
      cardColor = 'red';
      isMoveInDateBeforeAvailableDate = true;
      const days = startDate.diff(currentUserMoveInDate, 'days');
      tooptipLeftPosition = days > 7 ? 10 : (days * 100) / 7;
    } else if (currentUserMoveInDate.isSameOrBefore(endDate)) {
      cardTitle = (
        <div>
          Your move-in date <br /> of {currentUserMoveInDate.format(dateFormat)} works!
        </div>
      );
      cardEmoji = '😃';
      cardColor = 'green';
      tooptipLeftPosition =
        (currentUserMoveInDate.diff(startDate, 'days') * 100) / endDate.diff(startDate, 'days');
    } else if (currentUserMoveInDate.isSameOrBefore(extendedDate)) {
      cardTitle = (
        <div>
          Your move-in date <br />
          of {currentUserMoveInDate.format(dateFormat)} might work.
        </div>
      );
      cardEmoji = '🙂';
      cardColor = 'orange300';
      tooptipLeftPosition =
        (currentUserMoveInDate.diff(endDate, 'days') * 100) / extendedDate.diff(endDate, 'days');
    } else {
      cardTitle = (
        <div>
          Your move date of <br />
          {currentUserMoveInDate.format(dateFormat)} doesn’t work.
        </div>
      );
      cardEmoji = '👎';
      cardColor = 'red';
      const days = currentUserMoveInDate.diff(extendedDate, 'days');
      tooptipLeftPosition = days > 7 ? 93 : (days * 100) / 7;
    }

    let arrow = null;
    if (currentUserMoveInDate) {
      arrow = (
        <SrBox
          className={classnames(
            'moveInWindowUserDate bounce',
            tooptipLeftPosition === 0 && 'leftZero',
          )}
          left={`${Math.floor(tooptipLeftPosition)}%`}
        >
          <SrBox className="tooltip" bgColor={cardColor}>
            <SrText color="white" size="medium" weight="bold" lineHeight={1.54}>
              {currentUserMoveInDateFormatted}
            </SrText>
            <SrBox className={classnames('tooltipArrow', cardColor)} />
          </SrBox>
        </SrBox>
      );
    }

    return (
      <SrBox className="SrListingMoveInWindow">
        <SrText weight="bold" font="whitney-sc">
          {'move-in window'}
          {currentUserMoveInDate && (
            <span>
              <SrText font="whitney-sc" color="blue300" weight="bold">
                {' - '}
              </SrText>
              <SrText
                weight="bold"
                font="whitney-sc"
                color="blue300"
                cursor="pointer"
                decoration="underline"
                onClick={this._onOpenMoveInDateModal}
              >
                change my move date
              </SrText>
            </span>
          )}
        </SrText>
        {isPreLease && (
          <SrText
            display="block"
            color="black"
            size="14"
            weight="500"
            padding="12px 0 0"
            lineHeight="20px"
            letterSpacing="0.5px"
          >
            Move-in date subject to change due to construction. If delayed and you can’t move in,
            Sunroom will refund your application fee.
          </SrText>
        )}
        <SrBox height={13} />
        <SrCardOld>
          <SrBox
            className={classnames(
              'moveInWindowCardHeader',
              !currentUserMoveInDate && 'noMoveInDate',
            )}
          >
            <SrFlex justify="between">
              <SrBox>
                <SrText color={cardColor} weight="bold" size="2xl" leading="2xl">
                  {cardTitle}
                </SrText>
              </SrBox>
              <SrBox paddingTop={2}>
                <SrEmoji emoji={cardEmoji} big />
              </SrBox>
            </SrFlex>
            {currentUserMoveInDate && (
              <SrBox marginTop={4}>
                <SrText color={cardColor} font="whitney-sc" weight="medium" lineHeight={1.56}>
                  {'property accepts move-ins: '}
                  {`${startDateFormatted.toLowerCase()} - ${endDateFormatted.toLowerCase()}`}
                </SrText>
              </SrBox>
            )}
          </SrBox>
          <SrSeparator display={currentUserMoveInDate ? null : 'none'} />
          <SrBox
            className={classnames('moveInWindowCardBody', !currentUserMoveInDate && 'noMoveInDate')}
          >
            {currentUserMoveInDate && (
              <SrText font="whitney-sc" weight="bold" letterSpacing="0.5px">
                see where your move-in stands
              </SrText>
            )}
            <SrBox
              className={classnames('moveInWindowTimeline', {
                beforeAvailableDate: isMoveInDateBeforeAvailableDate,
                noMoveInDate: !currentUserMoveInDate,
              })}
            >
              <SrBox className="line ok">
                <SrBox className="bar" />
                {cardColor === 'green' && arrow}
              </SrBox>
              <SrBox className="line maybe">
                <SrBox className="bar" />
                {cardColor === 'orange300' && arrow}
              </SrBox>
              <SrBox className="line not">
                <SrBox className="bar" />
                {cardColor === 'red' && arrow}
              </SrBox>
            </SrBox>
            <SrBox
              className={classnames('moveInWindowTimelineDates', {
                beforeAvailableDate: isMoveInDateBeforeAvailableDate,
              })}
            >
              <SrText className="okText" size="medium" weight="medium">
                {startDateFormatted}
              </SrText>
              <SrText className="maybeText" size="medium" weight="medium">
                {endDateFormatted}
              </SrText>
              <SrText className="notText" size="medium" weight="medium">
                {extendedDateFormatted}
              </SrText>
            </SrBox>
          </SrBox>
          {currentUserMoveInDate && (
            <SrBox className="moveInWindowCardFooter">
              <SrLink to="/faq-move-in-date">
                <SrIcon className="question-mark-circle-small" />
              </SrLink>
            </SrBox>
          )}
        </SrCardOld>
      </SrBox>
    );
  }
}

SrListingMoveInWindow.displayName = 'SrListingMoveInWindow';

export default SrListingMoveInWindow;
